import React, { useState, useContext, useEffect } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { parsePhoneNumber, Metadata } from 'libphonenumber-js/core';
import min from 'libphonenumber-js/metadata.min.json';
import { Tabs, Tab, TabPanel, Asset, Button, ButtonIcon, ButtonsBlock } from '@hz-design-system/web-ui';
import LoginDialogContainer from '../../../components/LoginDialog/LoginDialogContainer';
import CreateAccountDialogContainer from '../../../components/CreateAccountDialog/CreateAccountDialogContainer';
import AdmarktRegistrationStepCompany from './AdmarktRegistrationStepCompany';
import AdmarktRegistrationStepRecommend from './AdmarktRegistrationStepRecommend';
import AdmarktRegistrationStepContact from './AdmarktRegistrationStepContact';
import AdmarktRegistrationStepLinkAccount from './AdmarktRegistrationStepLinkAccount';
import AdmarktRegistrationStepInvoice from './AdmarktRegistrationStepInvoice';
import registerProUserService from '../../../services/registerProUserService';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import { LOCALE_NL, STORE } from '../../../constants/constants';
import { getStoreFromSessionStorage, saveStoreinSessionStorage } from '../../../utils/sessionStorage';
import { captureUtmParams } from '../../../utils/utmStorage';
import trackEvent from '../../../utils/trackEvent';
import {
  ACTION_ACCOUNT_CONNECT_CLICK,
  CONFIRM_CLICK,
  ACTION_STEP_LOADED,
  ACTION_TAB_CLICKED,
  ACTION_CONTACT_PHONE_CLICK,
  CREATE_ACCOUNT_CLICK,
} from '../../../constants/gaConstants';
import admarktTabs from '../../../constants/admarktTabs';

import Classes from './AdmarktRegistrationSteps.module.scss';
import { LoggedInUser } from '../../../../../../types/AppState';
import type { FormData } from '../types';

const getTabIndex = (tabId: string) => admarktTabs.findIndex((tab) => tab.id === tabId);
const getTabId = (tabIndex: number) => admarktTabs[tabIndex]?.id;

type AdmarktRegistrationStepsProps = {
  initialActiveTabId: string;
  showLoginDialogOnLoad: boolean;
  loggedInUser: LoggedInUser;
  switchUserUrl: string;
  redirectTo2FA: boolean;
};

const AdmarktRegistrationSteps = ({
  showLoginDialogOnLoad,
  loggedInUser,
  switchUserUrl,
  redirectTo2FA,
  initialActiveTabId,
}: AdmarktRegistrationStepsProps) => {
  const i18n = useI18nContext();
  const {
    xsrfToken,
    links: { createAccountUrl, successUrl, twoFaUrl, recaptchaScriptUrl },
  } = useContext(EnvironmentContext);
  const prefilledCompanyName =
    loggedInUser?.companyRegistrationData?.companyRegistrationName || loggedInUser?.title || '';
  const prefilledCompanyKvk = loggedInUser?.companyRegistrationData?.companyRegistrationNumber || '';
  const userEmail = loggedInUser?.email;
  const [store, setStore] = useState<FormData>({
    loginEmail: userEmail,
    companyName: prefilledCompanyName,
    companyRegistrationNumber: prefilledCompanyKvk,
    firstName: '',
    lastName: '',
    contactEmail: userEmail || '',
    phoneNumber: loggedInUser?.phoneNumber || '',
    website: '',
    promoCode: '',
    streetName: loggedInUser?.digitalInvoiceDetails?.street || '',
    houseNumber: loggedInUser?.digitalInvoiceDetails?.houseNumber || '',
    postalCode: loggedInUser?.digitalInvoiceDetails?.postcode || '',
    city: loggedInUser?.digitalInvoiceDetails?.city || '',
    taxNumber: loggedInUser?.digitalInvoiceDetails?.vatNumber || '',
    name: '',
    password: '',
  });

  const [tabs, setTabs] = useState(admarktTabs);
  const [activeTab, setActiveTab] = useState(getTabIndex(initialActiveTabId));
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showCreateAccountDialog, setShowCreateAccountDialog] = useState(false);

  const enableTabs = (currentTab: string) => {
    const tabIndex = getTabIndex(currentTab);
    setTabs(
      admarktTabs.map((tab, index) => ({
        ...tab,
        disabled: index > tabIndex,
      })),
    );
    setActiveTab(tabIndex);
  };

  const nextTab = () => {
    setActiveTab(activeTab + 1);
    enableTabs(getTabId(activeTab + 1));
  };

  useEffect(() => {
    captureUtmParams();
  }, []);

  // prefill promoCode with possible query param value
  useEffect(() => {
    const promoCodeFromQpm = new URLSearchParams(window.location.search).get('promoCode');
    if (!promoCodeFromQpm) {
      return;
    }
    setStore({
      ...store,
      promoCode: promoCodeFromQpm,
    });
  }, []);

  useEffect(() => {
    if (initialActiveTabId) {
      setActiveTab(getTabIndex(initialActiveTabId));
    }
  }, [initialActiveTabId]);

  useEffect(() => {
    if (showLoginDialogOnLoad) {
      setShowLoginDialog(true);
    }
    const savedStore = getStoreFromSessionStorage(STORE);
    if (window.location.hash && savedStore) {
      const currentTab = window.location.hash.substr(1);
      enableTabs(currentTab);
      Object.keys(savedStore).forEach((k) => !savedStore[k] && delete savedStore[k]);
      setStore({
        ...store,
        ...savedStore,
      });
    }
  }, []);

  useEffect(() => {
    if (redirectTo2FA) {
      window.location.replace(twoFaUrl);
    }
  }, [redirectTo2FA, twoFaUrl]);

  useEffect(() => {
    const activeTabId = getTabId(activeTab);
    if (activeTabId) {
      trackEvent({ eventAction: ACTION_STEP_LOADED, eventLabel: activeTabId });
    }
    const currentHash = window.location.hash.substr(1);
    if (activeTabId && currentHash !== activeTabId) {
      window.location.hash = activeTabId;
    }
  }, [activeTab]);

  useEffect(() => {
    saveStoreinSessionStorage(STORE, store);
  }, [store]);

  const handleSubmit = (data: FormData) => {
    if (data.phoneNumber) {
      const metadataObj = new Metadata(min) as unknown as { metadata: any };
      const phoneNumber = parsePhoneNumber(data.phoneNumber, LOCALE_NL, metadataObj.metadata);
      data.phoneNumber = phoneNumber.number;
    }
    setStore({
      ...store,
      ...data,
    });
    nextTab();
  };

  const sendData = async (data: FormData) => {
    try {
      await registerProUserService({ xsrfToken, data });
    } catch (e) {
      console.warn(e);
    }

    window.location.href = successUrl;
  };

  const handleFinalSubmit = (data: FormData) => {
    trackEvent({ eventAction: CONFIRM_CLICK });
    setStore({
      ...store,
      ...data,
    });
    const dataToSend: Partial<FormData> = {};
    Object.keys(store).forEach((k) => {
      if (store[k as keyof FormData]) {
        dataToSend[k as keyof FormData] = store[k as keyof FormData];
      }
    });
    void sendData(dataToSend as FormData);
  };

  const removeShowLoginFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('showLoginDialog');

    const search = searchParams.size ? `?${searchParams.toString()}` : '';
    const url = window.location.pathname + search + window.location.hash;
    window.history.pushState({}, '', url);
  };

  const handleOnCloseLogin = () => {
    setShowLoginDialog(false);
    removeShowLoginFromUrl();
  };

  const handleOnCloseCreateAccount = () => {
    setShowCreateAccountDialog(false);
  };

  const handleShowLoginClick = () => {
    trackEvent({ eventAction: ACTION_ACCOUNT_CONNECT_CLICK, eventLabel: getTabId(activeTab) });
    setShowLoginDialog(true);
  };

  const handleCreateAccountClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    trackEvent({ eventAction: CREATE_ACCOUNT_CLICK, eventLabel: getTabId(activeTab) });
    window.open(createAccountUrl, '_blank');
  };

  const handleOnSuccessLogin = () => {
    removeShowLoginFromUrl();
    document.location.reload();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const data: { [key: string]: string } = {};
    data[e.target.id] = e.target.value;
    setStore({
      ...store,
      ...data,
    });
  };

  const onTabChange = (tabIndex: number) => {
    if (!tabs[tabIndex].disabled) {
      trackEvent({ eventAction: `${ACTION_TAB_CLICKED}_${getTabId(tabIndex)}`, eventLabel: getTabId(activeTab) });
      setActiveTab(tabIndex);
    }
  };

  const handleCallClick = () => {
    trackEvent({ eventAction: ACTION_CONTACT_PHONE_CLICK, eventLabel: getTabId(activeTab) });
    window.open('tel:0880082626', '_self');
  };

  return (
    <div className={Classes.root}>
      <div className={Classes.sidebar}>
        <div className={Classes.infoBlock}>
          <Trans tagName="h5" i18nKey="admarktRegistration.steps.info_block.title" className={Classes.blockTitle} />
          <ul className={Classes.reasonBlock}>
            <Trans tagName="li" i18nKey="admarktRegistration.steps.info_block.reason_budget" />
            <Trans tagName="li" i18nKey="admarktRegistration.steps.info_block.reason_fast" />
            <Trans tagName="li" i18nKey="admarktRegistration.steps.info_block.reason_costs" />
          </ul>

          <AdmarktRegistrationStepRecommend />
        </div>
        <div className={Classes.infoBlockTitle}>
          <Trans tagName="h3" i18nKey="admarktRegistration.steps.title" />
        </div>
        <div className={Classes.contactContainer}>
          <div className={Classes.contactBlock}>
            <Trans tagName="h5" i18nKey="admarktRegistration.steps.contact_block.title2" />
            <Trans tagName="p" i18nKey="admarktRegistration.steps.call_block.text" className={Classes.blockTitle} />
            <Button.Secondary className={Classes.buttonDesktop} onClick={handleCallClick}>
              <ButtonIcon name="call" isStartIcon />
              {i18n.t('admarktRegistration.steps.button.call')}
            </Button.Secondary>
            <ButtonsBlock className={Classes.buttonMobile} kind="adaptive">
              <Button.Primary onClick={handleCallClick}>
                <ButtonIcon name="call" isStartIcon />
                {i18n.t('admarktRegistration.steps.button.call_now')}
              </Button.Primary>
            </ButtonsBlock>
            <Trans tagName="p" i18nKey="admarktRegistration.steps.contact_block.text" className={Classes.blockTitle} />
          </div>
        </div>
      </div>
      <div className={Classes.section}>
        <div className={Classes.logoSection}>
          <Asset.Fluid alt="pro" name="pro" locale="nlnl" className={Classes.logo} />
        </div>
        <div className={Classes.tabsSection}>
          <Tabs className={Classes.tabs} activeTab={activeTab} setActiveTab={onTabChange} aria-label="Tabs">
            {tabs.map((tab, tabIndex) => (
              <Tab
                className={tab.disabled ? Classes.tabDisabled : ''}
                key={tab.id}
                label={
                  (
                    <>
                      {`${tabIndex + 1}. `}
                      <Trans tagName="span" i18nKey={`admarktRegistration.navigation.step.${tab.name}`} />
                    </>
                  ) as unknown as string
                }
              />
            ))}
          </Tabs>
          <div>
            <TabPanel activeTab={activeTab} tab={0}>
              <AdmarktRegistrationStepCompany
                userEmail={userEmail}
                store={store}
                switchUserUrl={switchUserUrl}
                onChange={handleChange}
                onSubmit={handleSubmit}
                handleShowLoginClick={handleShowLoginClick}
              />
            </TabPanel>
            <TabPanel activeTab={activeTab} tab={1}>
              <AdmarktRegistrationStepContact store={store} onChange={handleChange} onSubmit={handleSubmit} />
            </TabPanel>
            <TabPanel activeTab={activeTab} tab={2}>
              <AdmarktRegistrationStepInvoice store={store} onChange={handleChange} onSubmit={handleSubmit} />
            </TabPanel>
            <TabPanel activeTab={activeTab} tab={3}>
              <AdmarktRegistrationStepLinkAccount
                userEmail={userEmail}
                store={store}
                onFinalSubmit={handleFinalSubmit}
                handleShowLoginClick={handleShowLoginClick}
                handleCreateAccountClick={handleCreateAccountClick}
                loggedInUser={loggedInUser}
              />
            </TabPanel>
          </div>
          {showLoginDialog && <LoginDialogContainer onClose={handleOnCloseLogin} onSuccess={handleOnSuccessLogin} />}
          {showCreateAccountDialog && (
            <CreateAccountDialogContainer
              onClose={handleOnCloseCreateAccount}
              activeTabId={getTabId(activeTab)}
              store={store}
            />
          )}
          <script src={recaptchaScriptUrl} />
        </div>
      </div>
    </div>
  );
};

export default AdmarktRegistrationSteps;
